import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { FLEX_CONTAINER_COLUMN, FLEX_ITEM, H_100, PADDING, W_100 } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { createHtmlElement } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { CustomElement, WebPageComponent } from "@cervval.js/client-html5-spa/navigation";
import { ButtonStyle, CjsButton } from "@cervval.js/client-html5-ui/src/components/button/button";
import { CjsSmartTable } from "@cervval.js/client-html5-ui/src/components/smartTable/smartTable";
import { getSmartTablePropertyColumnsFromPropertyConfigs } from "@cervval.js/client-html5-ui/src/components/smartTable/smartTableColumn.tools";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { getLogConsole } from "@cervval.js/common/src/services/log.service";
import { anyObject } from "@cervval.js/common/src/tools/any.tools";
import { callCreateGeneratedFormDialog, callDeleteDialog } from "@ehealth_tools/client-html-tools/src/tools/dialog.tools";
import { addActionButtonToColumns, cellEditCallback, setColOption } from "@ehealth_tools/client-html-tools/src/tools/smartTable.tools";
import { getProductPropertyConfig } from "@ehealth_tools/common-booking/src/configs/product.propertyConfig";
import { BookingProduct } from "@ehealth_tools/common-booking/src/data/booking.dto";
import { BOOKING_SERVICE } from "@ehealth_tools/common-booking/src/services/abstractCommonBooking.service";
import { AbstractClientBookingService } from "../../../services/abstractClientBooking.service";
import { _I18nProductPage } from "./productPage.i18n";
@CustomElement({
    name: "product-page",
    style: require("./productPage.scss"),
    template: require("./productPage.html"),
    i18n: require("./productPage.i18n").I18nProductPage(),
    elementCssClasses: [W_100, H_100, FLEX_CONTAINER_COLUMN, FLEX_ITEM, PADDING.PY_2]
})
export class ProductPage<I18n extends _I18nProductPage = _I18nProductPage> extends WebPageComponent<I18n> {
    @TypedHtmlElements({ ensureLoadedClasses: [CjsSmartTable, CjsButton] })
    public readonly components!: {
        products: CjsSmartTable<BookingProduct>,
        addProduct: CjsButton
    };

    @Injected(BOOKING_SERVICE)
    private readonly _bookingService!: AbstractClientBookingService;


    /** */
    public override init(): Promise<void> | void {
        const columns = getSmartTablePropertyColumnsFromPropertyConfigs<BookingProduct>(getProductPropertyConfig());
        setColOption(columns, "price", {
            renderCell: (cell) => {
                if (cell.value == null) { return null; }
                return cell.value + "€";
            }
        });
        addActionButtonToColumns({
            columns: columns,
            btnOptions: { cssStyle: ButtonStyle.DANGER, iconMode: "only", icon: { name: "fa-trash" }, tooltip: { title: this.i18n.deleteProductTooltip } },
            disableAutoHide: () => true,
            uidSuffix: "_delete",
            callback: (data) => callDeleteDialog({
                dialogTitle: this.i18n.deleteProductDialogTitle,
                dtos: [data],
                tableName: this._bookingService.tables.bookingProducts,
                dialogHeaderContent: {
                    innerHTML: this.i18n.deleteProductHeaderHtmlTemplate(data.name)
                },
                options: {
                    callback: () => { this.refresh(); }
                },
                customDialogCssClass: "remove-product-dialog"
            }),
            width: "10%"
        });

        this.components.products.options.setAndRefreshIfChanged({
            columns,
            disableStrippedRows: true,
            isCellEditable: () => true
        });

        this.components.products.cellEditedValueEvent.on((e) => cellEditCallback(e, this._bookingService.dbDefinitions));

        this.components.addProduct.options.setAndRefreshIfChanged({
            icon: { name: "fa-plus", fixedWidth: true },
            label: this.i18n.addProduct,
            cssClasses: ["addProduct"]
        });

        this.components.addProduct.clickEvent.on(async _ => {
            try {
                const initProduct = await this._bookingService.initializeDTO("product");
                const proId = this._bookingService.getCurrentUser().id;
                callCreateGeneratedFormDialog({
                    dialogTitle: this.i18n.addProduct,
                    tablename: this._bookingService.tables.bookingProducts,
                    propertyConfigs: getProductPropertyConfig(),
                    options: {
                        defaultValues: {
                            ...initProduct,
                            professionalId: proId
                        } as anyObject,
                        callback: () => this.refresh()
                    },
                    dialogCustomClassList: ["dialog-product-creation"]
                });
            } catch (e) {
                getLogConsole().error(e);
            }
        });
    }

    /** @override */
    public override refresh(): void {
        const data = this._bookingService.getFilteredProducts(true).filter(v => v.professionalId === this._bookingService.getCurrentUser().id);
        this.components.products.setData({ data });
        if (data.length === 0) {
            this.components.products.components.tableBody.append(
                createHtmlElement("tr", { innerHTML: `<td colspan="${this.components.products.options.columns.length}" class="empty-td">${this.i18n.emptyTd}</td>` })
            );
        }
    }
}
